/**
 * AdminExperiencesPanel
 * ------------------------------------------------------------------
 * Moderation surface for host-submitted map "Experiences" (Phase 2).
 *
 * UX contract (from the product spec):
 *  - Full-screen hybrid satellite map at the top.
 *  - Two toggles under the header: **Pending** / **Approved**.
 *  - Category-colored dots with a tiny SVG glyph inside.
 *  - Tap a dot → open a card with host + photo + description (editable),
 *    submitted date, and tier selector (regular / premium) that MUST be
 *    picked before Approve.
 *  - Approve / Decline / Save / Delete actions.
 *  - Works well on mobile: the detail card becomes a bottom sheet that
 *    respects safe-area insets so buttons never sit under the nav bar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';

import { ADMIN_PALETTE, EmptyState } from './adminDesignSystem';
import {
  EXPERIENCE_CATEGORIES,
  getExperienceCategory,
} from '../../utils/experienceCategories';
import { ExperienceCategoryGlyph } from '../host/ExperienceCategoryGlyph';
import {
  approveAdminExperience,
  declineAdminExperience,
  deleteAdminExperience,
  fetchAdminExperiences,
  updateAdminExperience,
} from '../../utils/experiencesAdminApi';
import { ExperienceDetailCard } from './experiences/ExperienceDetailCard';
import { DeclineReasonModal, ToggleChip } from './experiences/ExperienceModals';

const HYBRID_STYLE = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Tiles © Esri',
      maxzoom: 19,
    },
    'esri-labels': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
    },
  },
  layers: [
    { id: 'satellite', type: 'raster', source: 'esri-satellite' },
    { id: 'labels', type: 'raster', source: 'esri-labels', paint: { 'raster-opacity': 0.9 } },
  ],
};

const GREECE_CENTER = [23.7275, 37.9838];

/**
 * Zoom-driven visual bands for admin experience markers. Mirrors the
 * guest map behaviour: at very low zoom we collapse the category pins to
 * plain dots / micro-dots so overlapping markers read as a cluster
 * instead of a vertical "domino line" of stacked pins.
 *
 * Admin map opens at zoom ~5.5 (country-wide), so the thresholds are
 * tuned lower than the guest map.
 */
function computeAdminZoomBand(zoom) {
  if (zoom >= 10) return 'full';
  if (zoom >= 7.5) return 'small';
  if (zoom >= 5.5) return 'dot';
  return 'micro-dot';
}

/**
 * Build the SVG-inside-a-dot marker HTML element for a category. Kept
 * outside the component so it doesn't rebuild on every render.
 * When `isFlagged`, the pin gets a warning ring so the moderator can
 * spot experiences trending negative without opening every card.
 */
function buildMarkerElement({ color, glyph, isSelected, isFlagged, testId, zoomBand = 'full' }) {
  const wrapper = document.createElement('button');
  wrapper.type = 'button';
  wrapper.setAttribute('data-testid', testId);
  wrapper.setAttribute('aria-label', 'Experience pin');
  // IMPORTANT: do NOT set `position: relative` on this outer element —
  // MapLibre relies on the `.maplibregl-marker` stylesheet rule
  // `position: absolute` to anchor the pin on its lng/lat. Overriding
  // that puts the button in document flow and lets layout reflow push
  // it around (user-reported "pin flies into the sea" bug). The flag
  // badge below uses `position: absolute` to float itself; the button
  // itself only sets its own background + cursor + flex layout.
  wrapper.style.cssText = `
    background: transparent; border: none; padding: 0; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  `;

  const dot = document.createElement('span');
  // Pin dimensions per zoom band — shrinks aggressively at low zoom so
  // overlapping pins no longer stack vertically into a visual line.
  const sizeByBand = {
    full: isSelected ? 34 : 28,
    small: isSelected ? 22 : 18,
    dot: 10,
    'micro-dot': 6,
  };
  const size = sizeByBand[zoomBand] ?? (isSelected ? 34 : 28);
  const borderWidth = zoomBand === 'micro-dot' ? 1 : (zoomBand === 'dot' ? 1.5 : 2.5);
  const flaggedShadow = '0 0 0 3px #EF4444, 0 0 0 6px rgba(239,68,68,0.30), 0 8px 20px rgba(15,17,21,0.28)';
  dot.style.cssText = `
    width: ${size}px; height: ${size}px; border-radius: 50%;
    background: ${color}; border: ${borderWidth}px solid #FFFFFF;
    display: flex; align-items: center; justify-content: center;
    box-shadow: ${isFlagged
      ? flaggedShadow
      : (isSelected
        ? `0 0 0 6px ${color}44, 0 8px 20px rgba(15,17,21,0.28)`
        : '0 4px 12px rgba(15,17,21,0.22)')};
    transition: transform 140ms ease, box-shadow 140ms ease;
    transform-origin: center;
  `;
  // Glyph is only legible on the two bigger bands — hide below.
  const showGlyph = zoomBand === 'full' || zoomBand === 'small';
  if (showGlyph) {
    const glyphSize = zoomBand === 'small' ? 10 : (isSelected ? 16 : 13);
    dot.innerHTML = `
      <svg width="${glyphSize}" height="${glyphSize}" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        ${renderGlyphPaths(glyph)}
      </svg>
    `;
  }
  wrapper.appendChild(dot);

  if (isFlagged) {
    const badge = document.createElement('span');
    badge.setAttribute('aria-label', 'Flagged — high dislike ratio');
    badge.setAttribute('data-testid', `${testId}-flag-badge`);
    badge.style.cssText = `
      position: absolute; top: -4px; right: -6px;
      width: 16px; height: 16px; border-radius: 50%;
      background: #EF4444; color: #FFFFFF;
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; font-weight: 900; line-height: 1;
      border: 2px solid #FFFFFF;
      box-shadow: 0 4px 10px rgba(239,68,68,0.35);
    `;
    badge.textContent = '!';
    wrapper.appendChild(badge);
  }

  wrapper.addEventListener('mouseenter', () => {
    dot.style.transform = 'scale(1.15)';
  });
  wrapper.addEventListener('mouseleave', () => {
    dot.style.transform = 'scale(1)';
  });
  return wrapper;
}

function renderGlyphPaths(glyph) {
  switch (glyph) {
    case 'wave':
      return '<path d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/><path d="M2 18c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/>';
    case 'mountain':
      return '<path d="M3 20l6-10 4 6 3-4 5 8H3z"/><circle cx="16" cy="7" r="1.4"/>';
    case 'boot':
      return '<path d="M6 4v10l-2 2v3h14v-3l-3-2V4z"/><path d="M10 8v3M14 8v3"/>';
    case 'camera':
      return '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7l2-3h4l2 3"/><circle cx="12" cy="13.5" r="3.5"/>';
    case 'column':
      return '<path d="M4 6h16M4 20h16"/><path d="M7 6v14M12 6v14M17 6v14"/>';
    case 'target':
      return '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1" fill="#FFFFFF"/>';
    default:
      return '<circle cx="12" cy="12" r="6"/>';
  }
}

// ---------------------------------------------------------------
// Cluster chip — rendered when 2+ experience pins project to the
// same screen area at the current zoom. Keeps country-wide zoom
// from showing a dominoed line of 4+ pins stacked over Athens.
// ---------------------------------------------------------------
function buildClusterElement({ count, isFlagged, testId, zoomBand }) {
  // IMPORTANT: MapLibre uses the OUTER element's `style.transform` to
  // position the marker geographically, and the `.maplibregl-marker`
  // stylesheet sets `position: absolute` so the chip sits exactly on
  // its coord. We must NEVER override those two properties on the outer
  // element — doing so (e.g. `position: relative`, or writing
  // `style.transform` on hover) wipes MapLibre's translate and snaps
  // the chip to the parent's origin (top-left corner), or lets layout
  // reflow the chip across the map. All visual effects live on
  // `inner` instead.
  const outer = document.createElement('div');
  outer.setAttribute('data-testid', testId);
  outer.style.cursor = 'pointer';

  const inner = document.createElement('button');
  inner.type = 'button';
  inner.setAttribute('aria-label', `${count} experiences — tap to zoom in`);
  const size = zoomBand === 'micro-dot' ? 20 : (zoomBand === 'dot' ? 24 : 28);
  inner.style.cssText = `
    background: #7C3AED; border: 2px solid #FFFFFF; padding: 0;
    width: ${size}px; height: ${size}px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #FFFFFF;
    font-family: 'Manrope', sans-serif; font-weight: 800; font-size: ${size >= 24 ? 12 : 10}px;
    box-shadow: ${isFlagged
      ? '0 0 0 3px #EF4444, 0 0 0 6px rgba(239,68,68,0.30), 0 8px 20px rgba(15,17,21,0.28)'
      : '0 0 0 4px rgba(124,58,237,0.28), 0 8px 20px rgba(15,17,21,0.28)'};
    transform-origin: center;
    transition: transform 140ms ease;
  `;
  inner.textContent = String(count);
  inner.addEventListener('mouseenter', () => { inner.style.transform = 'scale(1.1)'; });
  inner.addEventListener('mouseleave', () => { inner.style.transform = 'scale(1)'; });
  outer.appendChild(inner);
  return outer;
}

// Pixel-proximity clustering. Projects every experience to screen
// pixels at the current camera and greedily groups any within
// `thresholdPx` of each other. Returns `[{members:[exp...], lng, lat}, ...]`.
// Cheap enough to re-run on `moveend` — list is small (≤ a few hundred).
function clusterByPixelProximity(list, map, thresholdPx) {
  const points = list
    .filter((e) => typeof e.latitude === 'number' && typeof e.longitude === 'number')
    .map((e) => {
      const p = map.project([e.longitude, e.latitude]);
      return { exp: e, x: p.x, y: p.y };
    });
  const visited = new Array(points.length).fill(false);
  const groups = [];
  for (let i = 0; i < points.length; i += 1) {
    if (visited[i]) continue;
    const members = [points[i].exp];
    let sumLng = points[i].exp.longitude;
    let sumLat = points[i].exp.latitude;
    visited[i] = true;
    for (let j = i + 1; j < points.length; j += 1) {
      if (visited[j]) continue;
      const dx = points[i].x - points[j].x;
      const dy = points[i].y - points[j].y;
      if ((dx * dx + dy * dy) < thresholdPx * thresholdPx) {
        visited[j] = true;
        members.push(points[j].exp);
        sumLng += points[j].exp.longitude;
        sumLat += points[j].exp.latitude;
      }
    }
    groups.push({
      members,
      lng: sumLng / members.length,
      lat: sumLat / members.length,
    });
  }
  return groups;
}

// ---------------------------------------------------------------
// Category legend (small, wraps under the toggle row)
// ---------------------------------------------------------------
const CategoryLegend = () => (
  <div
    data-testid="admin-experiences-legend"
    style={{
      display: 'flex', flexWrap: 'wrap', gap: 8,
      padding: '8px 10px', background: ADMIN_PALETTE.surfaceMuted,
      border: `1px solid ${ADMIN_PALETTE.border}`, borderRadius: 12,
      alignItems: 'center',
    }}
  >
    <span style={{
      fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
      color: ADMIN_PALETTE.inkMuted, marginRight: 4,
    }}>
      Categories
    </span>
    {EXPERIENCE_CATEGORIES.map((cat) => (
      <span
        key={cat.slug}
        data-testid={`admin-experiences-legend-${cat.slug}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 9px', borderRadius: 9999,
          background: '#FFFFFF', border: `1px solid ${ADMIN_PALETTE.border}`,
          fontSize: 11.5, fontWeight: 700, color: ADMIN_PALETTE.inkSoft,
        }}
      >
        <span style={{
          width: 14, height: 14, borderRadius: 9999, background: cat.color,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ExperienceCategoryGlyph glyph={cat.glyph} size={9} strokeWidth={2.4} />
        </span>
        {cat.display}
      </span>
    ))}
  </div>
);

// ---------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------
export function AdminExperiencesPanel({ isMobileView = false, onCountsChange }) {
  const [status, setStatus] = useState('pending');
  const [experiences, setExperiences] = useState({ pending: [], approved: [] });
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  // Current zoom band (full / small / dot / micro-dot). We rebuild markers
  // on band change so pins shrink to tiny dots when the admin zooms all
  // the way out to see the whole country — stops the vertical "domino
  // line" of overlapping pins at country-level zoom.
  const [zoomBand, setZoomBand] = useState('full');

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const lastFitSignatureRef = useRef('');

  // ── Fetch pending + approved in parallel so switching tabs is instant.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [pending, approved] = await Promise.all([
          fetchAdminExperiences('pending'),
          fetchAdminExperiences('approved'),
        ]);
        if (!alive) return;
        setExperiences({ pending, approved });
        if (typeof onCountsChange === 'function') {
          onCountsChange({ pending: pending.length, approved: approved.length });
        }
      } catch (err) {
        if (!alive) return;
        toast.error(err.response?.data?.detail || 'Failed to load experiences');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshTick, onCountsChange]);

  const activeList = experiences[status] || [];
  const selected = useMemo(
    () => activeList.find((e) => e.id === selectedId) || null,
    [activeList, selectedId],
  );

  // ── Init map (once) ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: HYBRID_STYLE,
      center: GREECE_CENTER,
      zoom: 5.5,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    // Track zoom band so pin size follows the camera. We update on both
    // `zoom` (continuous) and `zoomend` (settle) and only set state when
    // the band actually changes — the useEffect that rebuilds markers
    // then runs at most once per band crossing, not per frame.
    const syncZoom = () => {
      const next = computeAdminZoomBand(map.getZoom());
      setZoomBand((prev) => (prev === next ? prev : next));
    };
    syncZoom();
    map.on('zoom', syncZoom);
    map.on('zoomend', syncZoom);

    // Watch the container for size changes (e.g. sidebar collapse,
    // rotation, admin nav flipping between mobile/desktop) so
    // maplibre repaints instead of showing a torn/blank canvas.
    const container = mapContainerRef.current;
    let ro = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        try { map.resize(); } catch { /* noop */ }
      });
      ro.observe(container);
    }
    const onResize = () => map.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      map.off('zoom', syncZoom);
      map.off('zoomend', syncZoom);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── Rebuild markers whenever the list, selection, or camera moves.
  // Clustering depends on pixel distance between pin projections, so we
  // re-run on `moveend` too — that way panning doesn't leave stale
  // cluster groupings and zooming correctly re-splits clusters as the
  // user zooms in.
  const [mapMoveTick, setMapMoveTick] = useState(0);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const bump = () => setMapMoveTick((t) => t + 1);
    map.on('moveend', bump);
    map.on('zoomend', bump);
    return () => {
      map.off('moveend', bump);
      map.off('zoomend', bump);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Cluster threshold scales with the pin band so dots cluster more
    // aggressively at country-wide zoom (fixes the "4 pins stacked
    // vertically into the sea" problem) but full pins still render
    // individually once the admin zooms in.
    const thresholdPx = zoomBand === 'full' ? 32
      : zoomBand === 'small' ? 28
      : zoomBand === 'dot' ? 36
      : 44; // micro-dot: cluster hard so a country view shows at most a few chips
    const groups = clusterByPixelProximity(activeList, map, thresholdPx);

    groups.forEach((group) => {
      if (group.members.length === 1) {
        const exp = group.members[0];
        const cat = getExperienceCategory(exp.category);
        const el = buildMarkerElement({
          color: cat?.color || '#7C3AED',
          glyph: cat?.glyph || 'target',
          isSelected: exp.id === selectedId,
          isFlagged: !!exp.flagged,
          testId: `admin-experience-pin-${exp.id}`,
          zoomBand,
        });
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          setSelectedId(exp.id);
          map.easeTo({ center: [exp.longitude, exp.latitude], zoom: Math.max(map.getZoom(), 12), duration: 500 });
        });
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([exp.longitude, exp.latitude])
          .addTo(map);
        markersRef.current.push(marker);
      } else {
        // Multi-member cluster → single chip at the group's centroid.
        // Tapping zooms in by ~2 levels so the cluster breaks apart.
        const anyFlagged = group.members.some((m) => !!m.flagged);
        const el = buildClusterElement({
          count: group.members.length,
          isFlagged: anyFlagged,
          testId: `admin-experience-cluster-${group.members.map((m) => m.id).join('-')}`,
          zoomBand,
        });
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          map.easeTo({
            center: [group.lng, group.lat],
            zoom: Math.min(map.getZoom() + 2, 15),
            duration: 500,
          });
        });
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([group.lng, group.lat])
          .addTo(map);
        markersRef.current.push(marker);
      }
    });

    // Auto-fit bounds ONLY when the list itself changes (not for selection).
    const signature = `${status}:${activeList.map((e) => e.id).join(',')}`;
    if (signature !== lastFitSignatureRef.current && activeList.length > 0) {
      lastFitSignatureRef.current = signature;
      if (activeList.length === 1) {
        map.easeTo({
          center: [activeList[0].longitude, activeList[0].latitude],
          zoom: 12, duration: 600,
        });
      } else {
        const bounds = new maplibregl.LngLatBounds();
        activeList.forEach((e) => {
          if (typeof e.latitude === 'number' && typeof e.longitude === 'number') {
            bounds.extend([e.longitude, e.latitude]);
          }
        });
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 60, duration: 800, maxZoom: 12 });
        }
      }
    }
  }, [activeList, selectedId, status, zoomBand, mapMoveTick]);

  // Clear selection when the user switches Pending ↔ Approved.
  useEffect(() => {
    setSelectedId('');
  }, [status]);

  // ── Actions ─────────────────────────────────────────────────────
  const handleApprove = useCallback(async ({ tier, description }) => {
    if (!selected) return;
    setSaving(true);
    try {
      // Save description first if edited, then approve. Two calls keep
      // the API dumb and avoids merging edit+approve semantics.
      if (description !== (selected.description || '')) {
        await updateAdminExperience(selected.id, { description });
      }
      await approveAdminExperience(selected.id, tier);
      toast.success('Approved — the host has been notified.');
      setSelectedId('');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    } finally {
      setSaving(false);
    }
  }, [selected]);

  const [declineReasonFor, setDeclineReasonFor] = useState(null); // selected experience being declined

  const handleDeclineOpen = useCallback(() => {
    if (!selected) return;
    setDeclineReasonFor(selected);
  }, [selected]);

  const handleDeclineConfirm = useCallback(async (reason) => {
    if (!declineReasonFor) return;
    setSaving(true);
    try {
      await declineAdminExperience(declineReasonFor.id, reason);
      toast.success('Declined — the host has been notified with the reason.');
      setDeclineReasonFor(null);
      setSelectedId('');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    } finally {
      setSaving(false);
    }
  }, [declineReasonFor]);

  const handleSave = useCallback(async ({ description, tier }) => {
    if (!selected) return;
    const patch = {};
    if (description !== (selected.description || '')) patch.description = description;
    if (tier !== (selected.tier || 'regular')) patch.tier = tier;
    if (!Object.keys(patch).length) return;
    setSaving(true);
    try {
      await updateAdminExperience(selected.id, patch);
      toast.success('Updated');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [selected]);

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    const ok = window.confirm('Delete this experience permanently? This cannot be undone.');
    if (!ok) return;
    setSaving(true);
    try {
      await deleteAdminExperience(selected.id);
      toast.success('Deleted');
      setSelectedId('');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }, [selected]);

  const pendingCount = experiences.pending?.length || 0;
  const approvedCount = experiences.approved?.length || 0;

  return (
    <div data-testid="admin-experiences-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <style>{`
        @keyframes adminExperiencesSheetIn {
          from { transform: translateY(28px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-spin { animation: adminExperiencesSpin 900ms linear infinite; }
        @keyframes adminExperiencesSpin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header + toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{
            margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 20,
            fontWeight: 700, color: ADMIN_PALETTE.ink,
          }}>
            Experiences
          </h2>
          <p style={{
            margin: '2px 0 0', fontSize: 12.5, color: ADMIN_PALETTE.inkMuted,
            fontFamily: "'Manrope', sans-serif",
          }}>
            Host-submitted map pins. Approve to publish for guests.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshTick((t) => t + 1)}
          data-testid="admin-experiences-refresh"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 10,
            border: `1px solid ${ADMIN_PALETTE.border}`, background: '#FFFFFF',
            color: ADMIN_PALETTE.inkSoft, fontFamily: "'Manrope', sans-serif",
            fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Status toggle chips */}
      <div
        data-testid="admin-experiences-status-toggle"
        role="tablist"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
      >
        <ToggleChip
          testId="admin-experiences-status-pending"
          active={status === 'pending'}
          onClick={() => setStatus('pending')}
          tone="pending"
          label="Pending"
          count={pendingCount}
        />
        <ToggleChip
          testId="admin-experiences-status-approved"
          active={status === 'approved'}
          onClick={() => setStatus('approved')}
          tone="active"
          label="Approved"
          count={approvedCount}
        />
      </div>

      {/* Legend */}
      <CategoryLegend />

      {/* Map + detail row */}
      <div style={{
        display: 'flex', gap: 14,
        alignItems: 'stretch',
        flexDirection: isMobileView ? 'column' : 'row',
      }}>
        <div
          data-testid="admin-experiences-map-wrap"
          style={{
            // NOTE: on mobile we live inside a flex-column parent whose
            // height is auto — using `flex: 1` there sets flex-basis to 0
            // and swallows the explicit height. Force `flex: none` in the
            // column layout so `height` wins; keep `flex: 1` on desktop
            // where the row layout distributes horizontal space.
            flex: isMobileView ? 'none' : 1,
            width: isMobileView ? '100%' : undefined,
            minWidth: 0,
            position: 'relative',
            height: isMobileView ? 'clamp(360px, 62dvh, 620px)' : 'clamp(480px, 74dvh, 820px)',
            borderRadius: 20, overflow: 'hidden',
            border: `1px solid ${ADMIN_PALETTE.border}`,
            background: '#0F1B2A',
            boxShadow: '0 16px 36px rgba(15,17,21,0.16)',
          }}
        >
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

          {loading ? (
            <div style={{
              position: 'absolute', top: 12, left: 12,
              padding: '6px 10px', borderRadius: 999,
              background: 'rgba(20,16,50,0.72)', color: '#FFF',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
            }}>
              <Loader2 size={12} className="animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && activeList.length === 0 ? (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <div style={{
                pointerEvents: 'auto', maxWidth: 380, margin: 20,
              }}>
                <EmptyState
                  tone={status === 'pending' ? 'pending' : 'active'}
                  title={status === 'pending' ? 'Inbox zero!' : 'No approved experiences yet'}
                  body={status === 'pending'
                    ? 'No experiences are waiting for review right now.'
                    : 'Approved experiences will appear here on the map.'}
                  dataTestId="admin-experiences-empty-state"
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Detail card — right column on desktop, bottom sheet on mobile */}
        {!isMobileView && selected ? (
          <ExperienceDetailCard
            experience={selected}
            onClose={() => setSelectedId('')}
            onApprove={handleApprove}
            onDecline={handleDeclineOpen}
            onSave={handleSave}
            onDelete={handleDelete}
            isMobileView={false}
            saving={saving}
          />
        ) : null}
      </div>

      {/* Mobile bottom sheet */}
      {isMobileView && selected ? (
        <>
          <div
            onClick={() => setSelectedId('')}
            data-testid="admin-experiences-sheet-backdrop"
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,17,21,0.45)',
              zIndex: 9400,
            }}
          />
          <ExperienceDetailCard
            experience={selected}
            onClose={() => setSelectedId('')}
            onApprove={handleApprove}
            onDecline={handleDeclineOpen}
            onSave={handleSave}
            onDelete={handleDelete}
            isMobileView
            saving={saving}
          />
        </>
      ) : null}

      {/* Decline reason modal — the admin MUST pick a reason so the
          host receives an actionable notification and can fix + resubmit. */}
      {declineReasonFor ? (
        <DeclineReasonModal
          saving={saving}
          onCancel={() => setDeclineReasonFor(null)}
          onConfirm={handleDeclineConfirm}
        />
      ) : null}
    </div>
  );
}

export default AdminExperiencesPanel;
